import { SeoMeta } from "@/utils/seo/SeoMeta";
import { PrintTab } from "@components/customizing/print/printTab/PrintTab";
import { useEffect, useMemo, useRef, useState } from "react";
import * as P from "@components/customizing/print/Print.styles";
import PrintCart from "@components/customizing/print/printCart/PrintCart";
import { PrintDesignService } from "@components/customizing/print/PrintDesignService";
import useUserInfo from "@hook/app/useUserInfo";
import Layout from "@components/layout/Layout";
import FilterList from "@components/filterList/FilterList";
import PrintInquiryModal from "@components/customizing/print/PrintInquiryModal";
import InquiryCompleteModal from "@components/elements/inquiryCompleteModal";
import { useRouter } from "next/router";
import { useQueryClient } from "@tanstack/react-query";
import {
  usePrintCartItemsQuery,
  usePrintProductListQuery,
} from "@hook/customizing/print/usePrintProductListQuery";
import { failAlert, successAlert } from "@common/alert";
import { QUERY_KEYS } from "@common/queryKeys";
import { inquirySet } from "@api/products";

const ProductPage = () => {
  const router = useRouter();
  const queryClient = useQueryClient();

  const listRef = useRef(null);
  const cartRef = useRef(null);

  const [inquiryInputs, setInquiryInputs] = useState({});
  const [openInquiryModal, setOpenInquiryModal] = useState(false);
  const [openInquiryComplete, setOpenInquiryComplete] = useState(false);

  const { userInfo, getUserInfo } = useUserInfo();
  const { data: productList } = usePrintProductListQuery();
  const { data: printCartItems = [] } = usePrintCartItemsQuery();

  // 카트 담기
  const addPrintCart = (product) => {
    if (!userInfo?.Member?.ukey) {
      failAlert("로그인 후 이용 가능합니다.");
      router.push("/login");
      return;
    }

    const currentCart =
      queryClient.getQueryData([QUERY_KEYS.CUSTOMIZING.PRINT.CART]) || [];

    if (currentCart.find((el) => el.pcode === product.pcode)) {
      failAlert("이미 담긴 상품입니다.");
      return;
    }

    const newCart = [{ ...product, cnt: 1 }, ...currentCart];

    // 장바구니 업데이트
    queryClient.setQueryData([QUERY_KEYS.CUSTOMIZING.PRINT.CART], newCart);
    sessionStorage.setItem("printCart", JSON.stringify(newCart));
    successAlert("장바구니에 담겼습니다.");
  };

  // 카트 담긴 상태
  const inCart = (product) =>
    printCartItems.some((el) => el.pcode === product.pcode);

  const onClickInquiry = async () => {
    setOpenInquiryModal((prev) => !prev);
    setInquiryInputs({});
  };

  const onChangeInquiry = (e, id) => {
    if (id.includes("file")) {
      setInquiryInputs({ ...inquiryInputs, [id]: e.target.files[0] });
      return;
    }
    if (id === "policy") {
      setInquiryInputs({ ...inquiryInputs, [id]: e.target.checked });
      return;
    }
    setInquiryInputs({ ...inquiryInputs, [id]: e.target.value });
  };

  const onClickInquiryComplete = () => {
    setOpenInquiryComplete((prev) => !prev);
  };

  const submitInquiry = async () => {
    if (
      !inquiryInputs.name ||
      !inquiryInputs.tel ||
      !inquiryInputs.cnt ||
      !inquiryInputs.usages ||
      !inquiryInputs.email ||
      !inquiryInputs.content
    ) {
      failAlert("항목을 전부 입력해주세요.");
      return;
    }
    if (!inquiryInputs.policy) {
      failAlert("유의사항에 동의해주세요.");
      return;
    }
    if (
      !inquiryInputs.tel.match(
        /^(0[2-8][0-5]?|01[01346-9])-?([1-9]{1}[0-9]{2,3})-?([0-9]{4})$/
      )
    ) {
      failAlert("사용가능한 전화번호를 입력해주세요.");
      return;
    }
    isLoading();
    const body = {
      modes: "InquirySet",
      division: "F",
      pcode: "P000001",
      ...inquiryInputs,
    };
    const res = await inquirySet(body);
    if (res.Code === "0000") {
      successAlert(res.Data?.Msg || "문의가 등록되었습니다.");
      onClickInquiryComplete();
      onClickInquiry();
      setInquiryInputs({});
      isLoaded();
    } else {
      failAlert("문의 등록에 실패했습니다.");
      isLoaded();
    }
  };

  useEffect(() => {
    getUserInfo();
  }, []);

  return (
    <>
      <Layout
        parent={"홈"}
        sub={"소량인쇄"}
        subTitle={"상품소개"}
        pageTitle={SeoMeta.smallPrintServiceProduct.title}
        pageDesc={SeoMeta.smallPrintServiceProduct.description}
        pageKeyword={SeoMeta.smallPrintServiceProduct.keywords}
      >
        <PrintTab />
        <div className="container" ref={listRef}>
          <P.NormalTitle>
            소량인쇄 <strong>상품 선택</strong>
          </P.NormalTitle>
          <FilterList
            productList={productList}
            inCart={inCart}
            addCart={addPrintCart}
          />
        </div>
        <PrintDesignService />
      </Layout>
      {openInquiryModal && (
        <PrintInquiryModal
          onClickInquiry={onClickInquiry}
          inquiryInputs={inquiryInputs}
          onChangeInquiry={onChangeInquiry}
          submitInquiry={submitInquiry}
        />
      )}
      {openInquiryComplete && (
        <InquiryCompleteModal onClickInquiryComplete={onClickInquiryComplete} />
      )}
    </>
  );
};

export default ProductPage;
